import { useAppStore } from '@/state-management';
import { normaliseDateToMonthYear } from '@/utils/normaliseDateToMonthYear/normaliseDateToMonthYear';
import { zodResolver } from '@hookform/resolvers/zod';
import { NumberTextField } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import { Grid, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import type { FieldValues } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import { schema } from './Medicare.schema';

export type MedicareProps = {
  editable?: boolean;
  defaultValues?: FieldValues;
  onSubmit?: (data: FieldValues) => void;
  isLoading?: boolean;
};

const StyledDatePicker = styled(DatePicker)({
  width: '100%',
});

export const Medicare = ({ defaultValues, editable, onSubmit, isLoading }: MedicareProps) => {
  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_IDENTIFICATION_INFO;
  const patientIdentificationInfo = useAppStore((state) => state.getPatientIdentificationInfo());
  const setPatientIhiVerification = useAppStore.use.setPatientIhiVerification();
  const setPatientIdentificationInfo = useAppStore.use.setPatientIdentificationInfo();

  // Format any dates with legacy formats prior to setting the defaultValues.
  // This prevents validation errors on page load, even when format is acceptable (but legacy)
  const medicareExpValue = patientIdentificationInfo?.medicareExpDate;
  const formattedMedicareExpDate = medicareExpValue
    ? normaliseDateToMonthYear(medicareExpValue)?.toFormat('MM/yyyy') ?? null
    : null;

  const updatedDefaultValues = {
    ...defaultValues,
    medicareExpDate: formattedMedicareExpDate,
  };

  const updatedPatientIdentificationInfo = {
    ...patientIdentificationInfo,
    medicareExpDate: formattedMedicareExpDate,
  };

  const formContext = useForm<FieldValues>({
    defaultValues: updatedDefaultValues,
    values: updatedPatientIdentificationInfo, // This allows the fields to be updated by new values from the external IHI response
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  const handleOnSubmit = (fieldValues: FieldValues) => {
    setPatientIdentificationInfo(fieldValues);

    setPatientIhiVerification(true);
    if (onSubmit) {
      onSubmit(fieldValues);
    }
  };

  return (
    <FormSection
      title="Medicare"
      formName={FORM_NAME}
      formContext={formContext}
      onSubmit={handleOnSubmit}
      isLoading={isLoading}
      data-testId={FORM_NAME}
    >
      <FormContainer formContext={formContext}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="medicare-form">
          <Grid item xs={12}>
            <Controller
              name="medicareNumber"
              control={formContext.control}
              render={({ field: { onChange, value } }) => {
                return (
                  <NumberTextField
                    label="Number"
                    fullWidth
                    inputProps={{
                      maxLength: 10,
                    }}
                    disabled={!isEffectivelyEditable}
                    value={value}
                    error={!!formContext.formState?.errors?.medicareNumber}
                    helperText={formContext.formState?.errors?.medicareNumber?.message?.toString() ?? ''}
                    onValueChange={(v: { value: string; floatValue?: number; formattedValue: string }) => {
                      onChange(v.value);
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="medicareExpDate"
              control={formContext.control}
              render={({ field: { onChange, value } }) => {
                const normalisedDate = typeof value === 'string' ? normaliseDateToMonthYear(value) : value;

                return (
                  <StyledDatePicker
                    label="Expiration"
                    value={normalisedDate ?? null}
                    onChange={(newValue) => {
                      onChange(newValue instanceof DateTime && newValue.isValid ? newValue.toFormat('MM/yyyy') : null);
                    }}
                    minDate={DateTime.now()}
                    format="MM/yyyy"
                    views={['month', 'year']}
                    disabled={!isEffectivelyEditable}
                    slotProps={{
                      textField: {
                        error: !!formContext.formState?.errors?.medicareExpDate,
                        helperText: formContext.formState?.errors?.medicareExpDate?.message?.toString() ?? '',
                      },
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="medicareRefNumber"
              control={formContext.control}
              render={({ field: { onChange, value } }) => {
                return (
                  <NumberTextField
                    label="Card IRN"
                    fullWidth
                    inputProps={{
                      maxLength: 2,
                    }}
                    disabled={!isEffectivelyEditable}
                    value={value}
                    error={!!formContext.formState?.errors?.medicareRefNumber}
                    helperText={formContext.formState?.errors?.medicareRefNumber?.message?.toString() ?? ''}
                    onValueChange={(v: { floatValue?: number }) => {
                      const newValue = v.floatValue ?? null;
                      onChange(newValue);
                    }}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
